import { useMemo } from 'react'

import { matchFilterNumber, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { AgencyProviderDeadlineGroupModel, AgencyProviderDeadlineGroupRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['agencyProviderDeadlineGroups']

type AgencyProviderDeadlineGroupFilters = {
  agency_id?: string | number,
  provider_id?: string | number,
}

type UseAgencyProviderDeadlineGroupsOptions = {
  entityKey?: string,
  filters?: AgencyProviderDeadlineGroupFilters,
  performHttpRequests?: boolean,
  requestOptions?: AgencyProviderDeadlineGroupRequestOptions,
}

function useAgencyProviderDeadlineGroups(options: UseAgencyProviderDeadlineGroupsOptions) {
  const { entityKey, filters = {}, requestOptions = {} } = options
  const {
    agency_id: filterAgencyId,
    provider_id: filterProviderId,
  } = filters

  const {
    updatedEntities: { agencyProviderDeadlineGroups: agencyProviderDeadlineGroupsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { agencyProviderDeadlineGroups } = useSelector(reduxState => reduxState.entities)

  const filteredAgencyProviderDeadlineGroups = useMemo((): AgencyProviderDeadlineGroupModel[] => {
    const filtered = Object.values(agencyProviderDeadlineGroups).filter((agencyProviderDeadlineGroup) => {
      const { agency_id, provider_id } = agencyProviderDeadlineGroup

      const matchAgencyId = matchFilterNumber(agency_id, filterAgencyId ? Number(filterAgencyId) : undefined)
      const matchProviderId = matchFilterNumber(provider_id, filterProviderId ? Number(filterProviderId) : undefined)

      return matchAgencyId && matchProviderId
    })

    return sortArrayBy(filtered, 'asc')
  }, [agencyProviderDeadlineGroupsUpdatedAt, JSON.stringify(filters)])

  const filteredAgencyProviderDeadlineGroupsCount = filteredAgencyProviderDeadlineGroups.length
  const hasFilteredAgencyProviderDeadlineGroups = !!filteredAgencyProviderDeadlineGroupsCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredAgencyProviderDeadlineGroupsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingAgencyProviderDeadlineGroups } = useReduxAction(
    'agencyProviderDeadlineGroups',
    'loadAgencyProviderDeadlineGroups',
    {
      entityKey,
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loadedForKeys, loading }: ModuleState) => {
        if (entityKey) return performHttpRequests && !loadedForKeys?.includes(entityKey) && !loading
        return performHttpRequests && !loading
      },
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredAgencyProviderDeadlineGroups,
    filteredAgencyProviderDeadlineGroupsCount,
    hasFilteredAgencyProviderDeadlineGroups,
    loading: loadingAgencyProviderDeadlineGroups,
  }
}

export default useAgencyProviderDeadlineGroups
